// ProgressBarThreshold.js

import { h } from 'preact';
import classNames from 'classnames';

import { useState, useEffect } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import {
  IconGWP1,
  IconGWP2,
  IconGWP3,
  IconGWP4,
  IconFreeShipping,
  IconCheck,
} from '../utils/icon';

const ProgressBarThreshold = ({
  cartSubtotal,
  thresholdData,
  setCurrentTier,
}) => {
  const [progress, setProgress] = useState(0);
  const [nextThreshold, setNextThreshold] = useState(null);

  useEffect(() => {
    const calculateProgress = () => {
      if (thresholdData.length === 0) return 0;

      const minThreshold = thresholdData[0].threshold;
      const maxThreshold = thresholdData[thresholdData.length - 1].threshold;

      setNextThreshold(null);
      let nextThresholdData;

      thresholdData.forEach((data, index) => {
        if (cartSubtotal < data.threshold && !nextThresholdData) {
          nextThresholdData = data;
          let currentTier = index > 0 ? thresholdData[index - 1] : false;
          if (currentTier?.shipping_threshold) {
            currentTier = index > 1 ? thresholdData[index - 2] : false;
          }
          setCurrentTier(currentTier);
        }
      });

      if (thresholdData[thresholdData.length - 1].threshold <= cartSubtotal) {
        setCurrentTier(thresholdData[thresholdData.length - 1]);
      }

      setNextThreshold(nextThresholdData);

      if (cartSubtotal >= maxThreshold) {
        return 100;
      }

      if (thresholdData.length === 1) {
        return (cartSubtotal / maxThreshold) * 100;
      }

      if (cartSubtotal <= minThreshold) {
        return 0;
      }

      return (
        ((cartSubtotal - minThreshold) / (maxThreshold - minThreshold)) * 100
      );
    };
    setProgress(calculateProgress());
  }, [cartSubtotal, thresholdData]);

  const progressWidth =
    thresholdData[0].threshold > cartSubtotal
      ? `${progress}%`
      : `calc(${progress}% + 25px)`;

  return (
    <div className="px-20 py-16">
      <div className="progress-bar relative">
        <div className="plot-points relative h-50 w-full">
          {thresholdData.map((data, index) => {
            const position =
              ((data.threshold - thresholdData[0].threshold) /
                (thresholdData[thresholdData.length - 1].threshold -
                  thresholdData[0].threshold)) *
              100;

            const isLastItem = index === thresholdData.length - 1;
            const leftStyle = isLastItem
              ? `calc(${position}% - 25px)`
              : `${position}%`; // Adjusted the position based on whether it's the last item

            let finalPosition;
            if (thresholdData.length === 1) {
              finalPosition = `calc(100% - 25px)`;
            } else {
              finalPosition = leftStyle;
            }

            let IconComponent;

            if (data.icon === 'gwp1') {
              IconComponent = IconGWP1;
            } else if (data.icon === 'gwp2') {
              IconComponent = IconGWP2;
            } else if (data.icon === 'gwp3') {
              IconComponent = IconGWP3;
            } else if (data.icon === 'gwp4') {
              IconComponent = IconGWP4;
            } else if (data.icon === 'free-shipping') {
              IconComponent = IconFreeShipping;
            }

            return (
              <div
                key={data.threshold}
                className={classNames(
                  'text-center',
                  'w-30',
                  'h-30',
                  'absolute',
                  data.threshold <= cartSubtotal
                    ? 'text-success'
                    : 'text-default',
                )}
                style={{ left: `${finalPosition}` }}
              >
                <IconComponent className={'h-30 w-30'} />
                <span
                  className={classNames(
                    'text-center',
                    'block',
                    'relative',
                    data.threshold <= cartSubtotal ? 'body-3-bold' : 'body-3',
                  )}
                >
                  ${data.threshold}
                  {data.threshold <= cartSubtotal ? (
                    <IconCheck
                      className={'absolute -right-6 top-[5px] h-auto w-8'}
                    />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-8 overflow-hidden rounded-full bg-gray-600">
          <div
            className="h-2 w-0 rounded-full bg-default transition-all"
            style={{ width: `${progressWidth}` }}
          ></div>
        </div>
      </div>
      {nextThreshold ? (
        <p className="body-3 mt-8 text-gray-900">
          {nextThreshold.shipping_message.replace(
            '{price}',
            formatMoney({ amount: nextThreshold.threshold - cartSubtotal }),
          )}
        </p>
      ) : null}
    </div>
  );
};

export default ProgressBarThreshold;
